/**
 * Access-token holder.
 *
 * The access token lives here in module memory and nowhere else — not
 * localStorage, not a readable cookie. It is short-lived by design, because a
 * JWT cannot be revoked: its lifetime is the window a stolen copy stays useful.
 *
 * What survives a reload is the refresh token, in an httpOnly cookie the
 * backend sets and JavaScript can never read. `refreshAccessToken()` trades
 * that cookie for a new access token; everything else here just holds the
 * result.
 */

const API = process.env.REACT_APP_BACKEND_URL;

let _accessToken = null;
let _inFlight = null;

/** The current access token, or null. Does not trigger a refresh. */
export function getToken() {
  return _accessToken;
}

export function setToken(token) {
  _accessToken = token;
}

export function clearToken() {
  _accessToken = null;
}

/**
 * Trade the refresh cookie for a fresh access token.
 *
 * Concurrent callers share one request: on a cold load several components ask
 * at once, and because every refresh *rotates* the token, firing them in
 * parallel would make the later ones replay an already-spent token — which the
 * backend correctly treats as theft and responds to by revoking the whole
 * session. Sharing the promise is what keeps that from happening.
 *
 * Returns the token, or null when there is no valid session.
 */
export function refreshAccessToken() {
  if (_inFlight) return _inFlight;

  _inFlight = fetch(`${API}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include', // the refresh cookie is cross-site; it must be asked for
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => {
      if (!res.ok) {
        _accessToken = null;
        return null;
      }
      return res.json().then(({ access_token: token }) => {
        _accessToken = token || null;
        return _accessToken;
      });
    })
    .catch(() => {
      _accessToken = null;
      return null;
    })
    .finally(() => {
      _inFlight = null;
    });

  return _inFlight;
}
